import { generatePath } from 'react-router-dom'
import { REPORTING_DOCUMENT_TYPES } from '@common/constants/client'
import { ROUTES } from '../constants/routes'

/**
 * Maps reporting document types to their corresponding route patterns
 */
export const REPORTING_DOCUMENT_TYPE_ROUTES: Record<string, string> = {
  [REPORTING_DOCUMENT_TYPES.financials]: ROUTES.ONGOING_REPORTING,
  [REPORTING_DOCUMENT_TYPES.financialProjections]: ROUTES.ONGOING_REPORTING,
  [REPORTING_DOCUMENT_TYPES.salesBySKU]: ROUTES.ONGOING_REPORTING_SALES_BY_SKU,
  [REPORTING_DOCUMENT_TYPES.arGeneralLedger]: ROUTES.ONGOING_REPORTING_AR_GENERAL_LEDGER,
  [REPORTING_DOCUMENT_TYPES.bankTransactions]: ROUTES.ONGOING_REPORTING_BANK_TRANSACTIONS_UPLOAD,
  [REPORTING_DOCUMENT_TYPES.capTable]: ROUTES.ONGOING_REPORTING_CAP_TABLE_UPLOAD,
  [REPORTING_DOCUMENT_TYPES.coi]: ROUTES.ONGOING_REPORTING_INSURANCE_PAGE,
  [REPORTING_DOCUMENT_TYPES.eop]: ROUTES.ONGOING_REPORTING_INSURANCE_PAGE,
  [REPORTING_DOCUMENT_TYPES.ucc]: ROUTES.ONGOING_REPORTING_CERTIFICATES_PAGE,
  [REPORTING_DOCUMENT_TYPES.gs]: ROUTES.ONGOING_REPORTING_CERTIFICATES_PAGE,
}

/**
 * Get the route for a reporting document based on its type and id
 * @param reportType - The type of the reporting document
 * @param id - The id of the reporting document
 * @param fallbackLink - Optional fallback link (e.g., boxLink) if route not found
 * @returns The generated route path or fallback link
 */
export const getReportingDocumentRoute = (
  reportType: string,
  id: string,
  fallbackLink?: string | null,
): string | null => {
  const routePattern = REPORTING_DOCUMENT_TYPE_ROUTES[reportType]

  if (routePattern) {
    return generatePath(routePattern, { id })
  }

  // Return fallback or summary route
  return fallbackLink || generatePath(ROUTES.ONGOING_REPORTING_SUMMARY, { id })
}

const SUBMIT_REPORTING_RETURN_TO_PARAM = 'returnTo'

/** Kept in the URL rather than router state so it survives reloads, new tabs and URL rewrites. */
export const buildSubmitReportingPath = (submitPath: string, returnTo: string): string =>
  `${submitPath}?${new URLSearchParams({ [SUBMIT_REPORTING_RETURN_TO_PARAM]: returnTo })}`

/** Only same-origin paths are honoured, so a crafted link cannot redirect off-site. The value is
 * re-read through the URL parser, which turns `/\host` and `/<tab>/host` into hosts and
 * `/.//host` into a protocol-relative `//host` path. */
export const getSubmitReportingReturnTo = (
  searchParams: URLSearchParams,
  fallback: string,
): string => {
  const returnTo = searchParams.get(SUBMIT_REPORTING_RETURN_TO_PARAM)
  if (!returnTo?.startsWith('/')) {
    return fallback
  }
  const { origin } = window.location
  let url: URL
  try {
    url = new URL(returnTo, origin)
  } catch {
    // Malformed values such as `//` or `/\` are a crafted link, not a destination.
    return fallback
  }
  const path = `${url.pathname}${url.search}${url.hash}`
  return url.origin === origin && !path.startsWith('//') ? path : fallback
}
